import React, { useState, useEffect, useMemo, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"
import { Helmet } from "react-helmet-async"
import {ArrowLeft, Save, Image as ImageIcon, Tag as TagIcon, FileText, Calendar, Eye, Plus, Trash2, GripVertical, Video, Package, HelpCircle, ChevronDown, ChevronUp} from 'lucide-react'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { lumi } from "../lib/lumi"
import toast from "react-hot-toast"

interface ContentBlock {
  id: string
  type: 'text' | 'image' | 'video' | 'product' | 'faq'
  content: string
  data?: any
}

interface Article {
  _id: string
  title: string
  titleEn?: string
  slug: string
  excerpt?: string
  content: string
  blocks?: ContentBlock[]
  category: "news" | "guide" | "beanstory" | "events"
  tags?: string[]
  featuredImage?: string
  images?: string[]
  relatedProducts?: string[]
  relatedStores?: string[]
  author?: string
  publishedAt?: string
  status: "draft" | "published" | "archived"
  isFeatured: boolean
  viewCount?: number
  seoTitle?: string
  seoDescription?: string
  creator?: string
  createdAt: string
  updatedAt: string
}

const ArticleEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const isEditMode = Boolean(id)
  const quillRef = useRef<ReactQuill>(null)

  const [loading, setLoading] = useState(isEditMode)
  const [saving, setSaving] = useState(false)
  const [products, setProducts] = useState<any[]>([])
  const [article, setArticle] = useState<Partial<Article>>({
    title: "",
    titleEn: "",
    slug: "",
    excerpt: "",
    content: "",
    blocks: [],
    category: "news",
    tags: [],
    featuredImage: "",
    images: [],
    relatedProducts: [],
    relatedStores: [],
    author: "",
    publishedAt: "",
    status: "draft",
    isFeatured: false,
    seoTitle: "",
    seoDescription: ""
  })

  const categories = [
    { value: "news", label: "最新消息", color: "bg-blue-600" },
    { value: "guide", label: "品鑑指南", color: "bg-green-600" },
    { value: "beanstory", label: "豆誌故事", color: "bg-purple-600" },
    { value: "events", label: "活動紀錄", color: "bg-orange-600" }
  ]

  // 載入商品列表
  useEffect(() => {
    fetchProducts()
  }, [])

  const fetchProducts = async () => {
    try {
      const response = await lumi.entities.products.list({})
      setProducts(response?.list || [])
    } catch (error) {
      console.error("Failed to fetch products:", error)
    }
  }

  // 自訂圖片上傳處理器
  const imageHandler = () => {
    const input = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    input.click()
    
    input.onchange = async () => {
      const file = input.files?.[0]
      if (!file) return
      
      const loadingToast = toast.loading('上傳圖片中...')
      
      try {
        const results = await lumi.tools.file.upload([file])
        const imageUrl = results[0]?.fileUrl
        
        if (imageUrl) {
          const quill = quillRef.current?.getEditor()
          if (quill) {
            const range = quill.getSelection(true)
            quill.insertEmbed(range.index, 'image', imageUrl)
            quill.setSelection(range.index + 1, 0)
          }
          toast.dismiss(loadingToast)
          toast.success('圖片上傳成功')
        } else {
          toast.dismiss(loadingToast)
          toast.error('圖片上傳失敗')
        }
      } catch (error) {
        console.error('Image upload error:', error)
        toast.dismiss(loadingToast)
        toast.error('圖片上傳失敗')
      }
    }
  }

  // Quill 編輯器配置
  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'header': [1, 2, 3, false] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        [{ 'indent': '-1'}, { 'indent': '+1' }],
        [{ 'align': [] }],
        ['link', 'image', 'video'],
        ['blockquote', 'code-block'],
        ['clean']
      ],
      handlers: {
        image: imageHandler
      }
    }
  }), [])

  const quillFormats = [
    'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'list', 'bullet', 'indent', 'align',
    'link', 'image', 'video', 'blockquote', 'code-block'
  ]

  useEffect(() => {
    if (isEditMode && id) {
      fetchArticle(id)
    }
  }, [id, isEditMode])

  const fetchArticle = async (articleId: string) => {
    setLoading(true)
    try {
      const response = await lumi.entities.articles.list({
        filter: { _id: articleId }
      })
      if (response && response.list && response.list.length > 0) {
        const fetchedArticle = response.list[0]
        // 如果沒有 blocks，初始化為空陣列
        if (!fetchedArticle.blocks) {
          fetchedArticle.blocks = []
        }
        setArticle(fetchedArticle)
      } else {
        throw new Error("文章不存在")
      }
    } catch (error) {
      console.error("Failed to fetch article:", error)
      toast.error("載入文章失敗")
      navigate("/admin/lab")
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!article.title || !article.slug || !article.content || !article.category) {
      toast.error("請填寫所有必填欄位")
      return
    }

    setSaving(true)
    
    const articleData = {
      ...article,
      updatedAt: new Date().toISOString(),
      createdAt: article.createdAt || new Date().toISOString(),
      creator: article.creator || "admin",
      viewCount: article.viewCount || 0
    }

    try {
      if (isEditMode && id) {
        await lumi.entities.articles.update(id, articleData)
        toast.success("文章更新成功")
      } else {
        await lumi.entities.articles.create(articleData)
        toast.success("文章創建成功")
      }
      navigate("/admin/lab")
    } catch (error) {
      console.error("Article operation failed:", error)
      toast.error(isEditMode ? "文章更新失敗" : "文章創建失敗")
    } finally {
      setSaving(false)
    }
  }

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>, type: "featured" | "gallery") => {
    const files = e.target.files
    if (!files || files.length === 0) return

    toast.loading("上傳中...")
    try {
      const results = await lumi.tools.file.upload(Array.from(files))
      const successUrls = results.filter(r => r.fileUrl).map(r => r.fileUrl as string)
      const failedCount = results.filter(r => r.uploadError).length

      if (successUrls.length > 0) {
        if (type === "featured") {
          setArticle(prev => ({ ...prev, featuredImage: successUrls[0] }))
        } else {
          setArticle(prev => ({ 
            ...prev, 
            images: [...(prev.images || []), ...successUrls] 
          }))
        }
        toast.dismiss()
        toast.success(`成功上傳 ${successUrls.length} 張圖片${failedCount > 0 ? `，${failedCount} 張失敗` : ""}`)
      } else {
        toast.dismiss()
        toast.error("所有圖片上傳失敗")
      }
    } catch (error) {
      toast.dismiss()
      toast.error("上傳失敗")
    }
  }

  const generateSlug = () => {
    if (!article.title) return
    const slug = article.title
      .toLowerCase()
      .replace(/[\s]+/g, "-")
      .replace(/[^\w\-\u4e00-\u9fa5]+/g, "")
      .substring(0, 50)
    setArticle(prev => ({ ...prev, slug }))
  }

  // ===== 區塊編輯功能 =====
  const addBlock = (type: ContentBlock['type']) => {
    const newBlock: ContentBlock = {
      id: Date.now().toString(),
      type,
      content: '',
      data: type === 'faq' ? { items: [{ question: '', answer: '' }] } : {}
    }
    setArticle(prev => ({
      ...prev,
      blocks: [...(prev.blocks || []), newBlock]
    }))
  }

  const updateBlock = (blockId: string, updates: Partial<ContentBlock>) => {
    setArticle(prev => ({
      ...prev,
      blocks: (prev.blocks || []).map(block =>
        block.id === blockId ? { ...block, ...updates } : block
      )
    }))
  }

  const deleteBlock = (blockId: string) => {
    setArticle(prev => ({
      ...prev,
      blocks: (prev.blocks || []).filter(block => block.id !== blockId)
    }))
  }

  const moveBlock = (blockId: string, direction: 'up' | 'down') => {
    setArticle(prev => {
      const blocks = [...(prev.blocks || [])]
      const index = blocks.findIndex(b => b.id === blockId)
      if (index === -1) return prev
      
      const newIndex = direction === 'up' ? index - 1 : index + 1
      if (newIndex < 0 || newIndex >= blocks.length) return prev
      
      [blocks[index], blocks[newIndex]] = [blocks[newIndex], blocks[index]]
      return { ...prev, blocks }
    })
  }

  const handleBlockImageUpload = async (blockId: string) => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.onchange = async (e: any) => {
      const file = e.target?.files?.[0]
      if (!file) return
      
      toast.loading('上傳中...')
      try {
        const results = await lumi.tools.file.upload([file])
        const imageUrl = results[0]?.fileUrl
        if (imageUrl) {
          updateBlock(blockId, { content: imageUrl })
          toast.dismiss()
          toast.success('圖片上傳成功')
        } else {
          toast.dismiss()
          toast.error('上傳失敗')
        }
      } catch (error) {
        toast.dismiss()
        toast.error('上傳失敗')
      }
    }
    input.click()
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>{isEditMode ? "編輯文章" : "新增文章"} - ONGU COFFEE SYSTEM</title>
      </Helmet>

      <div className="min-h-screen bg-gray-900">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-gray-950 border-b border-gray-800">
          <div className="max-w-7xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => navigate("/admin/lab")}
                  className="p-2 text-gray-400 hover:text-gray-100 hover:bg-gray-800 transition-colors"
                  title="返回列表"
                >
                  <ArrowLeft size={20} />
                </button>
                <div>
                  <h1 className="text-xl font-light tracking-wider text-gray-100">
                    {isEditMode ? "編輯文章" : "新增文章"}
                  </h1>
                  <p className="text-xs text-gray-500 mt-1">
                    {isEditMode ? `ID: ${id}` : "創建新文章"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/admin/lab")}
                  className="px-4 py-2 border border-gray-600 text-gray-300 hover:border-gray-400 hover:text-gray-100 transition-colors text-sm font-light tracking-wider"
                >
                  取消
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex items-center gap-2 px-6 py-2 bg-gray-400 text-black hover:bg-gray-300 disabled:bg-gray-600 disabled:text-gray-400 transition-colors text-sm font-light tracking-wider"
                >
                  <Save size={16} />
                  {saving ? "儲存中..." : isEditMode ? "更新文章" : "發布文章"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <form onSubmit={handleSubmit} className="max-w-7xl mx-auto px-6 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-[calc(100vh-120px)]">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-6 overflow-y-auto pr-4" style={{ maxHeight: 'calc(100vh - 120px)' }}>
              {/* Title */}
              <div className="bg-gray-950 border border-gray-800 p-6">
                <label className="block text-sm text-gray-400 mb-3">文章標題（中文）*</label>
                <input
                  type="text"
                  value={article.title || ""}
                  onChange={(e) => setArticle(prev => ({ ...prev, title: e.target.value }))}
                  onBlur={generateSlug}
                  required
                  className="w-full bg-gray-900 border border-gray-700 px-4 py-3 text-xl text-gray-100 focus:border-gray-500 focus:outline-none"
                  placeholder="輸入文章標題..."
                />
              </div>

              {/* Title EN & Slug */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-950 border border-gray-800 p-6">
                  <label className="block text-sm text-gray-400 mb-3">文章標題（英文）</label>
                  <input
                    type="text"
                    value={article.titleEn || ""}
                    onChange={(e) => setArticle(prev => ({ ...prev, titleEn: e.target.value }))}
                    className="w-full bg-gray-900 border border-gray-700 px-4 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                    placeholder="Article Title in English"
                  />
                </div>

                <div className="bg-gray-950 border border-gray-800 p-6">
                  <label className="block text-sm text-gray-400 mb-3">URL 標識符 *</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={article.slug || ""}
                      onChange={(e) => setArticle(prev => ({ ...prev, slug: e.target.value }))}
                      required
                      className="flex-1 bg-gray-900 border border-gray-700 px-4 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                      placeholder="coffee-brewing-guide"
                    />
                    <button
                      type="button"
                      onClick={generateSlug}
                      className="px-3 py-2 bg-gray-700 text-gray-300 hover:bg-gray-600 text-xs"
                      title="從標題生成"
                    >
                      生成
                    </button>
                  </div>
                </div>
              </div>

              {/* Excerpt */}
              <div className="bg-gray-950 border border-gray-800 p-6">
                <label className="block text-sm text-gray-400 mb-3">文章摘要</label>
                <textarea
                  value={article.excerpt || ""}
                  onChange={(e) => setArticle(prev => ({ ...prev, excerpt: e.target.value }))}
                  rows={3}
                  className="w-full bg-gray-900 border border-gray-700 px-4 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                  placeholder="簡短描述文章內容，用於列表和搜尋結果..."
                />
              </div>

              {/* Quill Editor - 保留原有文字編輯器 */}
              <div className="bg-gray-950 border border-gray-800 p-6">
                <label className="block text-sm text-gray-400 mb-3 flex items-center gap-2">
                  <FileText size={16} />
                  文章內容（富文本編輯器）*
                </label>
                <div className="quill-editor-dark">
                  <ReactQuill
                    ref={quillRef}
                    theme="snow"
                    value={article.content || ""}
                    onChange={(content) => setArticle(prev => ({ ...prev, content }))}
                    modules={quillModules}
                    formats={quillFormats}
                    className="min-h-[400px]"
                    placeholder="開始撰寫您的文章內容..."
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">使用富文本編輯器編輯內容，支援圖片、影片、表格等格式</p>
              </div>

              {/* 區塊編輯器 */}
              <div className="bg-gray-950 border border-gray-800 p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-light text-gray-100">內容區塊（進階）</h3>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => addBlock('text')}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-700 text-gray-300 hover:bg-gray-600 text-xs"
                      title="新增文字區塊"
                    >
                      <FileText size={14} />
                      文字
                    </button>
                    <button
                      type="button"
                      onClick={() => addBlock('image')}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-700 text-gray-300 hover:bg-gray-600 text-xs"
                      title="新增照片區塊"
                    >
                      <ImageIcon size={14} />
                      照片
                    </button>
                    <button
                      type="button"
                      onClick={() => addBlock('video')}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-700 text-gray-300 hover:bg-gray-600 text-xs"
                      title="新增影片區塊"
                    >
                      <Video size={14} />
                      影片
                    </button>
                    <button
                      type="button"
                      onClick={() => addBlock('product')}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-700 text-gray-300 hover:bg-gray-600 text-xs"
                      title="新增產品卡區塊"
                    >
                      <Package size={14} />
                      產品
                    </button>
                    <button
                      type="button"
                      onClick={() => addBlock('faq')}
                      className="flex items-center gap-1 px-3 py-1 bg-gray-700 text-gray-300 hover:bg-gray-600 text-xs"
                      title="新增FAQ區塊"
                    >
                      <HelpCircle size={14} />
                      FAQ
                    </button>
                  </div>
                </div>

                {/* 區塊列表 */}
                <div className="space-y-4">
                  {(article.blocks || []).map((block, index) => (
                    <div key={block.id} className="bg-gray-900 border border-gray-700 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <GripVertical size={16} className="text-gray-500" />
                          <span className="text-sm text-gray-400">
                            {block.type === 'text' && '📝 文字區塊'}
                            {block.type === 'image' && '🖼️ 照片區塊'}
                            {block.type === 'video' && '🎬 影片區塊'}
                            {block.type === 'product' && '📦 產品卡區塊'}
                            {block.type === 'faq' && '❓ FAQ 區塊'}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <button
                            type="button"
                            onClick={() => moveBlock(block.id, 'up')}
                            disabled={index === 0}
                            className="p-1 text-gray-400 hover:text-gray-200 disabled:opacity-30"
                            title="上移"
                          >
                            <ChevronUp size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveBlock(block.id, 'down')}
                            disabled={index === (article.blocks || []).length - 1}
                            className="p-1 text-gray-400 hover:text-gray-200 disabled:opacity-30"
                            title="下移"
                          >
                            <ChevronDown size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => deleteBlock(block.id)}
                            className="p-1 text-red-400 hover:text-red-300"
                            title="刪除"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>

                      {/* 文字區塊 */}
                      {block.type === 'text' && (
                        <div className="quill-editor-dark">
                          <ReactQuill
                            theme="snow"
                            value={block.content}
                            onChange={(content) => updateBlock(block.id, { content })}
                            modules={quillModules}
                            formats={quillFormats}
                            className="min-h-[200px]"
                            placeholder="使用富文本編輯器編輯內容，支援圖片、影片、格式化等..."
                          />
                        </div>
                      )}

                      {/* 照片區塊 */}
                      {block.type === 'image' && (
                        <div>
                          {block.content ? (
                            <div className="relative">
                              <img src={block.content} alt="Block" className="w-full h-48 object-cover border border-gray-600" />
                              <button
                                type="button"
                                onClick={() => updateBlock(block.id, { content: '' })}
                                className="absolute top-2 right-2 p-1 bg-red-600 text-white text-xs"
                              >
                                移除
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleBlockImageUpload(block.id)}
                              className="w-full py-8 border-2 border-dashed border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-300 transition-colors"
                            >
                              點擊上傳照片
                            </button>
                          )}
                        </div>
                      )}

                      {/* 影片區塊 */}
                      {block.type === 'video' && (
                        <div>
                          <input
                            type="text"
                            value={block.content}
                            onChange={(e) => updateBlock(block.id, { content: e.target.value })}
                            className="w-full bg-gray-800 border border-gray-600 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none text-sm mb-2"
                            placeholder="輸入 YouTube 或影片 URL（例：https://www.youtube.com/watch?v=...）"
                          />
                          {block.content && (
                            <div className="aspect-video">
                              <iframe
                                src={block.content.includes('youtube.com') || block.content.includes('youtu.be') 
                                  ? block.content.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')
                                  : block.content}
                                className="w-full h-full border border-gray-600"
                                allowFullScreen
                              />
                            </div>
                          )}
                        </div>
                      )}

                      {/* 產品卡區塊 */}
                      {block.type === 'product' && (
                        <div>
                          <select
                            value={block.content}
                            onChange={(e) => updateBlock(block.id, { content: e.target.value })}
                            className="w-full bg-gray-800 border border-gray-600 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none text-sm"
                          >
                            <option value="">選擇商品</option>
                            {products.map(product => (
                              <option key={product._id} value={product._id}>
                                {product.name} - ${product.price}
                              </option>
                            ))}
                          </select>
                          {block.content && (() => {
                            const selectedProduct = products.find(p => p._id === block.content)
                            return selectedProduct ? (
                              <div className="mt-3 p-3 bg-gray-800 border border-gray-600">
                                <div className="flex gap-3">
                                  {selectedProduct.images?.[0] && (
                                    <img src={selectedProduct.images[0]} alt={selectedProduct.name} className="w-20 h-20 object-cover" />
                                  )}
                                  <div className="flex-1">
                                    <h4 className="text-sm text-gray-100 font-medium">{selectedProduct.name}</h4>
                                    <p className="text-xs text-gray-400 mt-1">${selectedProduct.price}</p>
                                  </div>
                                </div>
                              </div>
                            ) : null
                          })()}
                        </div>
                      )}

                      {/* FAQ 區塊 */}
                      {block.type === 'faq' && (
                        <div className="space-y-3">
                          {(block.data?.items || []).map((item: any, idx: number) => (
                            <div key={idx} className="bg-gray-800 border border-gray-600 p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs text-gray-500">問題 {idx + 1}</span>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newItems = [...(block.data?.items || [])]
                                    newItems.splice(idx, 1)
                                    updateBlock(block.id, { data: { items: newItems } })
                                  }}
                                  className="text-red-400 hover:text-red-300 text-xs"
                                >
                                  刪除
                                </button>
                              </div>
                              <input
                                type="text"
                                value={item.question}
                                onChange={(e) => {
                                  const newItems = [...(block.data?.items || [])]
                                  newItems[idx] = { ...newItems[idx], question: e.target.value }
                                  updateBlock(block.id, { data: { items: newItems } })
                                }}
                                className="w-full bg-gray-900 border border-gray-600 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none text-sm mb-2"
                                placeholder="問題"
                              />
                              <textarea
                                value={item.answer}
                                onChange={(e) => {
                                  const newItems = [...(block.data?.items || [])]
                                  newItems[idx] = { ...newItems[idx], answer: e.target.value }
                                  updateBlock(block.id, { data: { items: newItems } })
                                }}
                                rows={3}
                                className="w-full bg-gray-900 border border-gray-600 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none text-sm"
                                placeholder="回答"
                              />
                            </div>
                          ))}
                          <button
                            type="button"
                            onClick={() => {
                              const newItems = [...(block.data?.items || []), { question: '', answer: '' }]
                              updateBlock(block.id, { data: { items: newItems } })
                            }}
                            className="w-full py-2 border border-dashed border-gray-600 text-gray-400 hover:border-gray-500 hover:text-gray-300 text-sm"
                          >
                            + 新增問答
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {(article.blocks || []).length === 0 && (
                    <div className="text-center py-8 text-gray-500 text-sm">
                      尚無區塊內容，點擊上方按鈕新增
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - Sidebar */}
            <div className="space-y-6 overflow-y-auto pr-4" style={{ maxHeight: 'calc(100vh - 120px)' }}>
              {/* Publish Settings */}
              <div className="bg-gray-950 border border-gray-800 p-6">
                <h3 className="text-lg font-light text-gray-100 mb-4">發布設定</h3>
                
                <div className="space-y-4">
                  {/* Status */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">狀態 *</label>
                    <select
                      value={article.status || "draft"}
                      onChange={(e) => setArticle(prev => ({ ...prev, status: e.target.value as Article["status"] }))}
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                    >
                      <option value="draft">草稿</option>
                      <option value="published">已發布</option>
                      <option value="archived">已封存</option>
                    </select>
                  </div>

                  {/* Category */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">分類 *</label>
                    <select
                      value={article.category || ""}
                      onChange={(e) => setArticle(prev => ({ ...prev, category: e.target.value as Article["category"] }))}
                      required
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                    >
                      <option value="">選擇分類</option>
                      {categories.map(cat => (
                        <option key={cat.value} value={cat.value}>{cat.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Author */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2">作者</label>
                    <input
                      type="text"
                      value={article.author || ""}
                      onChange={(e) => setArticle(prev => ({ ...prev, author: e.target.value }))}
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                      placeholder="ONGU Studio 編輯部"
                    />
                  </div>

                  {/* Publish Date */}
                  <div>
                    <label className="block text-sm text-gray-400 mb-2 flex items-center gap-2">
                      <Calendar size={14} />
                      發布日期
                    </label>
                    <input
                      type="datetime-local"
                      value={article.publishedAt ? new Date(article.publishedAt).toISOString().slice(0, 16) : ""}
                      onChange={(e) => setArticle(prev => ({ ...prev, publishedAt: e.target.value }))}
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                    />
                  </div>

                  {/* Featured */}
                  <div className="flex items-center justify-between py-2">
                    <label className="text-sm text-gray-400 flex items-center gap-2">
                      <Eye size={14} />
                      精選文章
                    </label>
                    <button
                      type="button"
                      onClick={() => setArticle(prev => ({ ...prev, isFeatured: !prev.isFeatured }))}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        article.isFeatured ? "bg-blue-600" : "bg-gray-700"
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          article.isFeatured ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>

              {/* Tags */}
              <div className="bg-gray-950 border border-gray-800 p-6">
                <label className="block text-sm text-gray-400 mb-3 flex items-center gap-2">
                  <TagIcon size={14} />
                  標籤
                </label>
                <input
                  type="text"
                  value={(article.tags || []).join(", ")}
                  onChange={(e) => setArticle(prev => ({ 
                    ...prev, 
                    tags: e.target.value.split(",").map(tag => tag.trim()).filter(tag => tag) 
                  }))}
                  className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-gray-100 focus:border-gray-500 focus:outline-none"
                  placeholder="咖啡豆, 手沖, 品鑑"
                />
                <p className="text-xs text-gray-500 mt-2">用逗號分隔多個標籤</p>
              </div>

              {/* Featured Image */}
              <div className="bg-gray-950 border border-gray-800 p-6">
                <label className="block text-sm text-gray-400 mb-3 flex items-center gap-2">
                  <ImageIcon size={14} />
                  特色圖片
                </label>
                
                {article.featuredImage && (
                  <div className="mb-3 relative group">
                    <img 
                      src={article.featuredImage} 
                      alt="Featured" 
                      className="w-full h-40 object-cover border border-gray-700"
                    />
                    <button
                      type="button"
                      onClick={() => setArticle(prev => ({ ...prev, featuredImage: "" }))}
                      className="absolute top-2 right-2 p-1 bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      ✕
                    </button>
                  </div>
                )}
                
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, "featured")}
                  className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:bg-gray-700 file:text-gray-100 hover:file:bg-gray-600"
                />
              </div>

              {/* Gallery Images */}
              <div className="bg-gray-950 border border-gray-800 p-6">
                <label className="block text-sm text-gray-400 mb-3">圖片集</label>
                
                {article.images && article.images.length > 0 && (
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {article.images.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img 
                          src={img} 
                          alt={`Gallery ${idx + 1}`} 
                          className="w-full h-20 object-cover border border-gray-700"
                        />
                        <button
                          type="button"
                          onClick={() => setArticle(prev => ({
                            ...prev,
                            images: prev.images?.filter((_, i) => i !== idx)
                          }))}
                          className="absolute top-1 right-1 p-1 bg-red-600 text-white text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleImageUpload(e, "gallery")}
                  className="block w-full text-sm text-gray-400 file:mr-4 file:py-2 file:px-4 file:border-0 file:text-sm file:bg-gray-700 file:text-gray-100 hover:file:bg-gray-600"
                />
              </div>

              {/* Related Content */}
              <div className="bg-gray-950 border border-gray-800 p-6">
                <h3 className="text-sm text-gray-400 mb-3">相關內容</h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">相關商品 ID</label>
                    <input
                      type="text"
                      value={(article.relatedProducts || []).join(", ")}
                      onChange={(e) => setArticle(prev => ({ 
                        ...prev, 
                        relatedProducts: e.target.value.split(",").map(id => id.trim()).filter(id => id) 
                      }))}
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 focus:border-gray-500 focus:outline-none"
                      placeholder="product1, product2"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">相關分店 ID</label>
                    <input
                      type="text"
                      value={(article.relatedStores || []).join(", ")}
                      onChange={(e) => setArticle(prev => ({ 
                        ...prev, 
                        relatedStores: e.target.value.split(",").map(id => id.trim()).filter(id => id) 
                      }))}
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 focus:border-gray-500 focus:outline-none"
                      placeholder="xinyi, renai"
                    />
                  </div>
                </div>
              </div>

              {/* SEO Settings */}
              <div className="bg-gray-950 border border-gray-800 p-6">
                <h3 className="text-sm text-gray-400 mb-3">SEO 設定</h3>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">SEO 標題</label>
                    <input
                      type="text"
                      value={article.seoTitle || ""}
                      onChange={(e) => setArticle(prev => ({ ...prev, seoTitle: e.target.value }))}
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 focus:border-gray-500 focus:outline-none"
                      placeholder="留空則使用文章標題"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-xs text-gray-500 mb-2">SEO 描述</label>
                    <textarea
                      value={article.seoDescription || ""}
                      onChange={(e) => setArticle(prev => ({ ...prev, seoDescription: e.target.value }))}
                      rows={3}
                      className="w-full bg-gray-900 border border-gray-700 px-3 py-2 text-sm text-gray-100 focus:border-gray-500 focus:outline-none"
                      placeholder="留空則使用文章摘要"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  )
}

export default ArticleEditor
